<template>
  <!-- A project of the registry: new (POST) or edited (PATCH with the changed fields). -->
  <form
    class="ai-project"
    @submit.prevent="save"
  >
    <div class="ai-project__head">
      <button
        type="button"
        class="ai-link-button"
        @click="$emit('cancel')"
      >
        <ai-icon
          name="chevron"
          :size="14"
          class="ai-project__back"
        />
        Реестр проектов
      </button>
      <h4>{{ project ? `Проект ${project.code}` : 'Новый проект' }}</h4>
    </div>

    <div class="ai-project__grid">
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-code"
        >
          Шифр <span class="ai-field__hint">обязательно</span>
        </label>
        <input
          id="ai-project-code"
          ref="code"
          v-model.trim="form.code"
          class="ai-input"
          maxlength="60"
          placeholder="15-2024"
          required
        >
      </div>
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-status"
        >
          Статус
        </label>
        <select
          id="ai-project-status"
          v-model="form.status"
          class="ai-input"
        >
          <option value="active">
            Действует
          </option>
          <option value="closed">
            Закрыт
          </option>
        </select>
      </div>
      <div class="ai-field is-wide">
        <label
          class="ai-field__label"
          for="ai-project-name"
        >
          Название
        </label>
        <input
          id="ai-project-name"
          v-model.trim="form.name"
          class="ai-input"
          maxlength="300"
          placeholder="Жилой комплекс «Северный», корпус 2"
        >
      </div>
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-customer"
        >
          Заказчик
        </label>
        <input
          id="ai-project-customer"
          v-model.trim="form.customer"
          class="ai-input"
          maxlength="200"
        >
      </div>
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-address"
        >
          Адрес объекта
        </label>
        <input
          id="ai-project-address"
          v-model.trim="form.address"
          class="ai-input"
          maxlength="300"
        >
      </div>
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-from"
        >
          Начало работ
        </label>
        <input
          id="ai-project-from"
          v-model="form.dateFrom"
          type="date"
          class="ai-input"
        >
      </div>
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-to"
        >
          Окончание
        </label>
        <input
          id="ai-project-to"
          v-model="form.dateTo"
          type="date"
          class="ai-input"
          :min="form.dateFrom || null"
        >
      </div>
    </div>

    <div class="ai-project__lists">
      <div
        v-for="field in lists"
        :key="field.key"
        class="ai-field"
      >
        <label
          class="ai-field__label"
          :for="`ai-project-${field.key}`"
        >
          {{ field.label }}
          <span class="ai-field__hint">{{ lineCount(form[field.key]) }}</span>
        </label>
        <textarea
          :id="`ai-project-${field.key}`"
          v-model="form[field.key]"
          class="ai-input"
          rows="3"
          :placeholder="field.placeholder"
        />
        <span class="ai-field__hint">{{ field.hint }}</span>
      </div>
      <div class="ai-field">
        <label
          class="ai-field__label"
          for="ai-project-note"
        >
          Примечание
        </label>
        <textarea
          id="ai-project-note"
          v-model="form.note"
          class="ai-input"
          rows="2"
          maxlength="1000"
        />
      </div>
    </div>

    <p
      v-if="project && project.updatedAt"
      class="ai-project__muted"
    >
      Изменён {{ changedAt }}
    </p>
    <p
      v-if="error"
      class="ai-project__error"
      role="alert"
    >
      {{ error }}
    </p>

    <div class="ai-project__actions">
      <confirm-inline
        v-if="project && deleting"
        class="ai-project__delete"
        :text="`Удалить проект ${project.code} из реестра?`"
        :busy="saving"
        @confirm="remove"
        @cancel="deleting = false"
      />
      <button
        v-else-if="project"
        type="button"
        class="ai-button is-ghost ai-project__delete"
        :disabled="saving"
        @click="deleting = true"
      >
        <ai-icon
          name="trash"
          :size="14"
        />
        Удалить
      </button>
      <button
        type="button"
        class="ai-button"
        @click="$emit('cancel')"
      >
        Отмена
      </button>
      <button
        type="submit"
        class="ai-button is-primary"
        :disabled="saving || !form.code || (project && !dirty)"
      >
        <span
          v-if="saving"
          class="ai-spinner ai-spinner--small"
        />
        {{ project ? 'Сохранить' : 'Добавить' }}
      </button>
    </div>
  </form>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import ConfirmInline from './ConfirmInline.vue';
  import { count } from './format';
  import { authorLabel, memoryDate } from './memory';

  const LISTS = [
    {
      key: 'folders',
      label: 'Рабочие папки',
      placeholder: '\\\\server\\projects\\15-2024',
      hint: '\\\\server\\projects\\15-2024 или P:\\15-2024; * — любая папка',
    },
    {
      key: 'people',
      label: 'Ответственные',
      placeholder: 'Орлов Пётр Андреевич',
      hint: 'ФИО, «Фамилия И.О.» или логин',
    },
    {
      key: 'recipients',
      label: 'Разрешённые получатели',
      placeholder: '@partner.example.com',
      hint: 'адрес, @домен, домен или контакт мессенджера; USB — разрешить копирование на носители',
    },
    {
      key: 'aliases',
      label: 'Другие названия и шифры',
      placeholder: 'ЖК Северный',
      hint: 'прежние шифры, сокращения и названия, под которыми проект встречается в файлах',
    },
  ];
  const TEXTS = ['code', 'name', 'address', 'customer', 'status', 'dateFrom', 'dateTo', 'note'];
  const KEYS = [...TEXTS, ...LISTS.map((field) => field.key)];

  const lines = (text) =>
    String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

  function formOf(project) {
    const source = project || {};
    const form = {};
    TEXTS.forEach((key) => {
      form[key] = source[key] == null ? '' : String(source[key]);
    });
    form.status = source.status || 'active';
    LISTS.forEach(({ key }) => {
      form[key] = (source[key] || []).join('\n');
    });
    return form;
  }

  // Values in the shape of the API: lists as arrays, empty dates as null.
  function bodyOf(form) {
    const body = {};
    TEXTS.forEach((key) => {
      body[key] = form[key].trim();
    });
    body.dateFrom = form.dateFrom || null;
    body.dateTo = form.dateTo || null;
    LISTS.forEach(({ key }) => {
      body[key] = lines(form[key]);
    });
    return body;
  }

  export default {
    name: 'ProjectForm',
    components: { AiIcon, ConfirmInline },
    props: {
      // The edited project; null for a new one.
      project: { type: Object, default: null },
    },
    data() {
      return { form: formOf(this.project), lists: LISTS, saving: false, error: '', deleting: false };
    },
    computed: {
      // Fields that differ from the saved project (the PATCH body).
      changes() {
        const saved = bodyOf(formOf(this.project));
        const current = bodyOf(this.form);
        return KEYS.filter((key) => JSON.stringify(saved[key]) !== JSON.stringify(current[key])).reduce(
          (result, key) => ({ ...result, [key]: current[key] }),
          {},
        );
      },
      dirty() {
        return Object.keys(this.changes).length > 0;
      },
      changedAt() {
        const author = authorLabel(this.project.updatedBy, this.$store.getters['user/userId']);
        return `${memoryDate(this.project.updatedAt)}${author ? `, ${author}` : ''}`;
      },
    },
    mounted() {
      if (!this.project) this.$refs.code.focus();
    },
    methods: {
      lineCount(text) {
        const total = lines(text).length;
        return total ? count(total, ['значение', 'значения', 'значений']) : 'по одному в строке';
      },
      validate() {
        const { dateFrom, dateTo } = this.form;
        if (dateFrom && dateTo && dateTo < dateFrom) return 'Дата окончания раньше даты начала.';
        return '';
      },
      async save() {
        this.error = this.validate();
        if (this.error) return;
        this.saving = true;
        try {
          const { data } = this.project
            ? await api.updateProject(this.project.id, this.changes)
            : await api.createProject(bodyOf(this.form));
          this.$emit('saved', data);
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось сохранить проект');
        } finally {
          this.saving = false;
        }
      },
      async remove() {
        this.saving = true;
        this.error = '';
        try {
          await api.deleteProject(this.project.id);
          this.$emit('deleted', this.project);
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось удалить проект');
          this.deleting = false;
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-project {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ai-project__head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    h4 {
      margin: 0;
      color: var(--ui-text);
      font-size: 15px;
      font-weight: 500;
    }
  }

  .ai-project__back {
    transform: rotate(180deg);
  }

  .ai-project__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px 12px;

    .is-wide {
      grid-column: 1 / -1;
    }
  }

  .ai-project__lists {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
  }

  .ai-project__muted,
  .ai-project__error {
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-project__error {
    color: var(--ui-danger);
  }

  .ai-project__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  .ai-project__delete {
    margin-right: auto;
  }
</style>
